// src/pages/AnnouncementDetails.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  collection,
  addDoc,
  query,
  getDoc,
  doc,
  onSnapshot,
  orderBy,
  serverTimestamp,
  updateDoc,
  increment,
  writeBatch,
  getDocs,
  where
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import {
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Paper,
  InputAdornment
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Share as ShareIcon,
  Reply as ReplyIcon,
  Send as SendIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';

// Fonction pour formater la date relative
const timeAgo = (date) => {
  if (!date) return '';
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'À l\'instant';
  
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Il y a ${minutes} min`;
  
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Il y a ${hours} h`;
  
  const days = Math.floor(hours / 24);
  if (days < 7) return `Il y a ${days} j`;
  
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short'
  });
};

// Sous-composant pour afficher les réponses d'un commentaire
function RepliesList({ announcementId, commentId }) {
  const [replies, setReplies] = useState([]);

  useEffect(() => {
    const ref = collection(
      db,
      'announcements',
      announcementId,
      'comments',
      commentId,
      'replies'
    );
    const q = query(ref, orderBy('createdAt'));
    return onSnapshot(q, snap =>
      setReplies(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [announcementId, commentId]);

  return (
    <Box ml={6} mt={1}>
      {replies.map(r => (
        <Box key={r.id} mb={1.5}>
          <Box display="flex" alignItems="center" mb={0.5}>
            <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: '#3f51b5' }}>
              {r.pseudo?.[0] || '?'}
            </Avatar>
            <Typography variant="subtitle2" fontWeight="bold">
              {r.pseudo}
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
              {r.createdAt?.toDate ? timeAgo(r.createdAt.toDate()) : ''}
            </Typography>
          </Box>
          <Paper 
            elevation={0} 
            sx={{ 
              backgroundColor: '#f0f2f5', 
              borderRadius: '18px', 
              p: 1.5,
              ml: 4 
            }}
          >
            <Typography variant="body2">{r.text}</Typography>
          </Paper>
        </Box>
      ))}
    </Box>
  );
}

export default function AnnouncementDetails() {
  const { id } = useParams();
  const [announcement, setAnnouncement] = useState(null);
  const [likesCount, setLikesCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [userData, setUserData] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  // Charge l'annonce et l'état du like
  useEffect(() => {
    async function load() {
      const docSnap = await getDoc(doc(db, 'announcements', id));
      if (!docSnap.exists()) return;
      const data = docSnap.data();
      setAnnouncement(data);
      setLikesCount(data.likesCount || 0);

      const uid = auth.currentUser?.uid;
      if (uid) {
        const likeQ = query(
          collection(db, 'likes'),
          where('announcementId', '==', id),
          where('userId', '==', uid)
        );
        const snap = await getDocs(likeQ);
        setLiked(!snap.empty);
      }
    }
    load();
  }, [id]);

  // Écoute des commentaires
  useEffect(() => {
    const ref = collection(db, 'announcements', id, 'comments');
    const q = query(ref, orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap =>
      setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [id]);

  // Charge le pseudo de l'utilisateur
  useEffect(() => {
    async function loadUser() {
      const u = auth.currentUser;
      if (!u) return;
      
      // Essayez d'abord users, puis admins
      const userSnap = await getDoc(doc(db, 'users', u.uid));
      if (userSnap.exists()) {
        setUserData(userSnap.data());
        return;
      }
      
      const adminSnap = await getDoc(doc(db, 'admins', u.uid));
      if (adminSnap.exists()) {
        setUserData(adminSnap.data());
      }
    }
    loadUser();
  }, []);

  // Like / unlike
  const toggleLike = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    
    if (liked) {
      // Supprimer le like
      const likeQ = query(
        collection(db, 'likes'),
        where('announcementId', '==', id),
        where('userId', '==', uid)
      );
      const batch = writeBatch(db);
      const snap = await getDocs(likeQ);
      snap.forEach(ds => batch.delete(doc(db, 'likes', ds.id)));
      batch.update(doc(db, 'announcements', id), { likesCount: increment(-1) });
      await batch.commit();
      setLikesCount(c => c - 1);
      setLiked(false);
    } else {
      await addDoc(collection(db, 'likes'), {
        announcementId: id,
        userId: uid,
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'announcements', id), {
        likesCount: increment(1),
      });
      setLikesCount(c => c + 1);
      setLiked(true);
    }
  };

  // Poster un commentaire
  const postComment = async () => {
    if (!newComment.trim() || !auth.currentUser) return;
    await addDoc(collection(db, 'announcements', id, 'comments'), {
      text: newComment,
      userId: auth.currentUser.uid,
      pseudo: userData?.pseudo || 'Anonyme',
      createdAt: serverTimestamp(),
    });
    setNewComment('');
  };

  // Poster une réponse
  const postReply = async commentId => {
    if (!replyText.trim() || !auth.currentUser) return;
    await addDoc(
      collection(db, 'announcements', id, 'comments', commentId, 'replies'),
      {
        text: replyText,
        userId: auth.currentUser.uid,
        pseudo: userData?.pseudo || 'Anonyme',
        createdAt: serverTimestamp(),
      }
    );
    setReplyText('');
    setReplyingTo(null);
  };

  if (!announcement) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography variant="h6">Chargement de l'annonce...</Typography>
      </Box>
    );
  }

  return (
    <Box maxWidth={800} mx="auto" p={{ xs: 2, md: 4 }}>
      {/* Carte de l'annonce */}
      <Card elevation={0} sx={{ borderRadius: '10px', mb: 3 }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: '#3f51b5' }}>
              {announcement.author?.[0] || 'A'}
            </Avatar>
          }
          action={
            <IconButton>
              <MoreVertIcon />
            </IconButton>
          }
          title="Administrateur"
          subheader={
            announcement.createdAt?.toDate 
              ? timeAgo(announcement.createdAt.toDate())
              : ''
          }
        />
        
        <CardContent>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {announcement.title}
          </Typography>
          <Typography variant="body1" paragraph>
            {announcement.description}
          </Typography>
          
          <Box display="flex" alignItems="center" mt={2}>
            <Chip
              icon={liked ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
              label={likesCount}
              color={liked ? 'primary' : 'default'}
              size="small"
              onClick={toggleLike}
              sx={{ mr: 1 }}
            />
            <Typography variant="body2" color="textSecondary">
              {comments.length} commentaires
            </Typography>
          </Box>
        </CardContent>
        
        <Divider />
        
        {/* Barre d'actions */}
        <Box display="flex" justifyContent="space-around" p={1}>
          <Button 
            startIcon={liked ? <FavoriteIcon color="primary" /> : <FavoriteBorderIcon />}
            onClick={toggleLike}
            sx={{ color: liked ? '#1976d2' : 'inherit' }}
          >
            J'aime
          </Button>
          <Button 
            startIcon={<ReplyIcon />}
            onClick={() => document.getElementById('comment-input')?.focus()}
          >
            Commenter
          </Button>
          <Button startIcon={<ShareIcon />}>
            Partager
          </Button>
        </Box>
      </Card>

      {/* Section des commentaires */}
      <Card elevation={0} sx={{ borderRadius: '10px' }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Commentaires ({comments.length})
          </Typography>
          
          {comments.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary">
                Soyez le premier à commenter
              </Typography>
            </Box>
          )}
          
          {comments.map(c => (
            <Box key={c.id} mb={3}>
              <Box display="flex" alignItems="flex-start">
                <Avatar sx={{ width: 32, height: 32, mt: 0.5, mr: 2, bgcolor: '#3f51b5' }}>
                  {c.pseudo?.[0] || '?'}
                </Avatar>
                
                <Box flexGrow={1}>
                  <Box display="flex" alignItems="center" mb={0.5}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {c.pseudo}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                      {c.createdAt?.toDate ? timeAgo(c.createdAt.toDate()) : ''}
                    </Typography>
                  </Box>
                  
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      backgroundColor: '#f0f2f5', 
                      borderRadius: '18px', 
                      p: 1.5 
                    }}
                  >
                    <Typography variant="body2">{c.text}</Typography>
                  </Paper>
                  
                  <Box display="flex" mt={0.5} ml={1}>
                    <Button 
                      size="small" 
                      startIcon={<ReplyIcon fontSize="small" />}
                      onClick={() => setReplyingTo(replyingTo === c.id ? null : c.id)}
                    >
                      Répondre
                    </Button>
                  </Box>
                  
                  {/* Champ de réponse */}
                  {replyingTo === c.id && (
                    <Box display="flex" mt={1} alignItems="flex-start">
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          mt: 0.5, 
                          mr: 1.5,
                          bgcolor: '#3f51b5'
                        }}
                      >
                        {userData?.pseudo?.[0] || '?'}
                      </Avatar>
                      
                      <Box flexGrow={1}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={1}
                          value={replyText}
                          onChange={e => setReplyText(e.target.value)}
                          placeholder="Écrire une réponse..."
                          variant="outlined"
                          size="small"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton 
                                  onClick={() => postReply(c.id)}
                                  disabled={!replyText.trim()}
                                  size="small"
                                >
                                  <SendIcon color="primary" />
                                </IconButton>
                              </InputAdornment>
                            ),
                            sx: { borderRadius: '20px', backgroundColor: '#f0f2f5' }
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                  
                  {/* Réponses */}
                  <RepliesList announcementId={id} commentId={c.id} />
                </Box>
              </Box>
            </Box>
          ))}
          
          {/* Champ de nouveau commentaire */}
          <Box display="flex" mt={3} alignItems="flex-start">
            <Avatar 
              sx={{ 
                width: 40, 
                height: 40, 
                mt: 0.5, 
                mr: 2,
                bgcolor: '#3f51b5'
              }}
            >
              {userData?.pseudo?.[0] || '?'}
            </Avatar>
            
            <Box flexGrow={1}>
              <TextField
                id="comment-input"
                fullWidth
                multiline
                minRows={2}
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Écrire un commentaire..."
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton 
                        onClick={postComment}
                        disabled={!newComment.trim()}
                      >
                        <SendIcon color="primary" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { borderRadius: '20px', backgroundColor: '#f0f2f5' }
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}